import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { formatDateDisplay } from '../../utils/dateUtils';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';

const userId = localStorage.getItem('userId');

const SERVICES = [
  {
    key: 'vetclinic',
    titleKey: 'vetclinic_operations',
    fallback: 'العيادة البيطرية',
  },
  {
    key: 'farrier',
    titleKey: 'farrier_operations',
    fallback: 'البيطار',
  },
  {
    key: 'accommodation',
    titleKey: 'accommodation_operations',
    fallback: 'الإيواء',
  },
  {
    key: 'transportation',
    titleKey: 'horse_transport.transport_operations',
    fallback: 'النقل',
  },
  {
    key: 'reproduction',
    titleKey: 'reproduction_clinic',
    fallback: 'عيادة الإنتاج',
  },
  {
    key: 'competitions',
    titleKey: 'competitions',
    fallback: 'Competitions',
  },
];

const moneyFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

function formatDate(value) {
  return formatDateDisplay(value);
}

function DateText({ value }) {
  return <span className="date-ltr">{formatDate(value)}</span>;
}

function formatMoney(value) {
  return moneyFormatter.format(Number(value || 0));
}

function isEntered(row) {
  return Boolean(row.reviewed_by_accountant);
}

function statusLabel(t, row) {
  return isEntered(row)
    ? t('entered_accounting_system') || 'مدخل في المحاسبة'
    : t('not_entered_accounting_system') || 'غير مدخل';
}

function serviceLabel(t, service) {
  const cfg = SERVICES.find((item) => item.key === service);
  return t(cfg?.titleKey || service) || cfg?.fallback || service;
}

function getAmount(row) {
  return Number(row.total_amount ?? row.amount ?? 0);
}

function isMonthlyAccommodation(row) {
  return row.billing_type === 'monthly_accommodation';
}

function isPartialAccommodation(row) {
  return row.billing_type === 'partial_accommodation';
}

function isAccommodationBilling(row) {
  return isMonthlyAccommodation(row) || isPartialAccommodation(row);
}

function accommodationBillingLabel(t, row) {
  if (isPartialAccommodation(row)) {
    return t('partial_accommodation_invoice') || 'Partial accommodation period';
  }
  return t('monthly_accommodation_invoice') || 'Monthly accommodation invoice';
}

function getRowKey(service, row, index) {
  return [
    service,
    row.record_id,
    row.horse_id,
    row.client_id,
    row.review_id,
    index,
  ]
    .filter((part) => part !== undefined && part !== null && part !== '')
    .join('-');
}

function normalizeRows(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    reviewed_by_accountant: Boolean(row.reviewed_by_accountant),
  }));
}

export default function AccountantReview() {
  const { t } = useTranslation();
  const [activeService, setActiveService] = useState('vetclinic');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [data, setData] = useState(() =>
    SERVICES.reduce((acc, service) => ({ ...acc, [service.key]: [] }), {})
  );
  const [loading, setLoading] = useState(false);
  const [serviceErrors, setServiceErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [dialogRecord, setDialogRecord] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    const nextData = {};
    const nextErrors = {};

    await Promise.all(
      SERVICES.map(async (service) => {
        try {
          const { data: rows } = await api.get(
            `/accountant-reviews/${service.key}`
          );
          nextData[service.key] = normalizeRows(rows);
        } catch (error) {
          nextData[service.key] = [];
          nextErrors[service.key] =
            error?.response?.data?.error || error?.message || 'Load failed';
        }
      })
    );

    setData(nextData);
    setServiceErrors(nextErrors);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const summary = useMemo(() => {
    return SERVICES.reduce((acc, service) => {
      const rows = data[service.key] || [];
      const entered = rows.filter(isEntered).length;
      const totalAmount = rows.reduce((sum, row) => sum + getAmount(row), 0);
      acc[service.key] = {
        total: rows.length,
        entered,
        pending: rows.length - entered,
        totalAmount,
      };
      return acc;
    }, {});
  }, [data]);

  const totalSummary = useMemo(() => {
    return SERVICES.reduce(
      (acc, service) => {
        const item = summary[service.key] || {};
        acc.total += item.total || 0;
        acc.entered += item.entered || 0;
        acc.pending += item.pending || 0;
        acc.amount += item.totalAmount || 0;
        return acc;
      },
      { total: 0, entered: 0, pending: 0, amount: 0 }
    );
  }, [summary]);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    let rows = [...(data[activeService] || [])];

    if (statusFilter === 'entered') rows = rows.filter(isEntered);
    if (statusFilter === 'pending') rows = rows.filter((row) => !isEntered(row));

    if (needle) {
      rows = rows.filter((row) =>
        [
          row.record_id,
          row.horse_name,
          row.invoice_number,
          row.notes,
          row.from_location,
          row.to_location,
          row.transaction_type,
          row.direction,
          row.paid_by,
          row.received_by,
          row.competition_name,
          row.class_name,
          row.championship_name,
          row.owner_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(needle)
      );
    }

    rows.sort((a, b) => {
      const aTime = new Date(a.record_date || a.start_date || 0).getTime();
      const bTime = new Date(b.record_date || b.start_date || 0).getTime();
      return sortOrder === 'desc' ? bTime - aTime : aTime - bTime;
    });

    return rows;
  }, [activeService, data, search, sortOrder, statusFilter]);

  const pagedRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const updateRow = (recordId, patch) => {
    setData((prev) => ({
      ...prev,
      [activeService]: prev[activeService].map((row) =>
        row.record_id === recordId ? { ...row, ...patch } : row
      ),
    }));
  };

  const saveRow = async (row) => {
    if (row.invoice_number && row.invoice_number.length < 3) {
      setSnackbar({
        open: true,
        message: t('invoice_number_min_warning'),
        severity: 'warning',
      });
      return;
    }

    const payload = {
      invoice_number: row.invoice_number || '',
      reviewed_by_accountant: Boolean(row.reviewed_by_accountant),
      notes: row.notes || '',
    };

    try {
      if (activeService === 'competitions') {
        await api.put(`/competitions/financial-transactions/${row.transaction_id || row.record_id}/review`, {
          invoice_number: payload.invoice_number,
          accountant_review_status: payload.reviewed_by_accountant ? 'REVIEWED' : 'PENDING',
          reviewed_by_accountant: payload.reviewed_by_accountant,
          notes: payload.notes,
        });
        setSnackbar({
          open: true,
          message: t('competition_financial_transaction_saved') || t('saved_successfully') || 'Saved successfully',
          severity: 'success',
        });
        fetchData();
        return;
      }

      if (row.review_id) {
        await api.put(`/accountant-reviews/${row.review_id}`, {
          ...payload,
          modified_by: userId,
        });
      } else {
        const { data: created } = await api.post('/accountant-reviews', {
          record_id: row.record_id,
          service_type: activeService,
          created_by: userId,
          ...payload,
        });
        updateRow(row.record_id, { review_id: created.review_id });
      }

      setSnackbar({
        open: true,
        message: t('saved_successfully') || 'تم الحفظ بنجاح',
        severity: 'success',
      });
      fetchData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('save_error') || 'تعذر الحفظ',
        severity: 'error',
      });
    }
  };

  const markEnteredAndSave = (row) => {
    const next = { ...row, reviewed_by_accountant: true };
    updateRow(row.record_id, { reviewed_by_accountant: true });
    saveRow(next);
  };

  const exportToExcel = async () => {
    const wb = new ExcelJS.Workbook();

    SERVICES.forEach((service) => {
      const rows = data[service.key] || [];
      if (!rows.length) return;

      const cleaned = rows.map((row) => ({
        service: serviceLabel(t, service.key),
        record_id: row.record_id,
        date: formatDate(row.record_date || row.start_date),
        horse_name: row.horse_name,
        period:
          row.period_start || row.period_end
            ? `${formatDate(row.period_start)} - ${formatDate(row.period_end)}`
            : '',
        accommodation_type: row.type_name_ar || row.type_name_en || '',
        amount: getAmount(row),
        vat: row.vat_amount || '',
        status: statusLabel(t, row),
        invoice_number: row.invoice_number || '',
        notes: row.notes || '',
      }));

      const ws = wb.addWorksheet(service.key.toUpperCase());
      const headers = Object.keys(cleaned[0]);
      ws.addRow(headers);
      cleaned.forEach((r) => ws.addRow(headers.map((h) => r[h])));
      headers.forEach((_, i) => {
        ws.getColumn(i + 1).width = 22;
      });
    });

    const wbout = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([wbout], { type: 'application/octet-stream' }),
      'AccountantReview.xlsx'
    );
  };

  const activeSummary = summary[activeService] || {
    total: 0,
    entered: 0,
    pending: 0,
    totalAmount: 0,
  };

  return (
    <Box
      className="smart-page-container"
      sx={{
        width: '100%',
        maxWidth: 'var(--app-page-max)',
        mx: 'auto',
        bgcolor: COLORS.page,
        overflowX: 'hidden',
      }}
    >
      <Stack spacing={2}>
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            p: 2,
            background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={2}
          >
            <Box>
              <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('accountant_review_title') || 'مركز مراجعة المحاسب'}
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.muted }}>
                {t('accountant_review_hint') ||
                  'علّم العمليات التي تم إدخالها يدويًا في نظام المحاسبة الخارجي.'}
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchData}
                disabled={loading}
                sx={{
                  borderColor: COLORS.line,
                  color: COLORS.text,
                  fontWeight: 800,
                  '&:hover': { borderColor: COLORS.gold, bgcolor: COLORS.cardSoft },
                }}
              >
                {t('refresh') || 'تحديث'}
              </Button>
              <Button
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={exportToExcel}
                sx={{
                  backgroundColor: COLORS.gold,
                  fontWeight: 900,
                  '&:hover': { backgroundColor: COLORS.goldHover },
                }}
              >
                {t('export_to_excel') || 'تصدير Excel'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 2,
          }}
        >
          <SummaryCard
            title={t('all_operations') || 'كل العمليات'}
            value={totalSummary.total}
            helper={`${formatMoney(totalSummary.amount)} SAR`}
          />
          <SummaryCard
            title={t('entered_accounting_system') || 'مدخل في المحاسبة'}
            value={totalSummary.entered}
            helper={`${totalSummary.total ? Math.round((totalSummary.entered / totalSummary.total) * 100) : 0}%`}
          />
          <SummaryCard
            title={t('pending_accounting_entry') || 'بانتظار الإدخال'}
            value={totalSummary.pending}
            helper={t('needs_accountant_action') || 'يحتاج إجراء'}
            warning={totalSummary.pending > 0}
          />
          <SummaryCard
            title={t('active_service') || 'الخدمة الحالية'}
            value={serviceLabel(t, activeService)}
            helper={`${activeSummary.pending} ${t('pending') || 'معلق'}`}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={activeService}
            onChange={(_, value) => {
              setActiveService(value);
              setPage(0);
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: `1px solid ${COLORS.line}`,
              bgcolor: COLORS.cardSoft,
              p: 1,
              minHeight: 88,
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                minHeight: 72,
                alignItems: 'flex-start',
                border: `1px solid ${COLORS.line}`,
                borderRadius: 2,
                mx: 0.5,
                px: 2,
                bgcolor: COLORS.card,
                color: COLORS.text,
                cursor: 'pointer',
                transition: 'all 160ms ease',
                '&:hover': {
                  borderColor: COLORS.gold,
                  bgcolor: '#fffaf0',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 8px 18px rgba(91, 75, 39, 0.08)',
                },
                '&.Mui-selected': {
                  borderColor: COLORS.gold,
                  bgcolor: COLORS.goldSoft,
                  color: COLORS.text,
                  boxShadow: 'inset 0 0 0 1px rgba(183,155,91,0.35)',
                },
              },
            }}
          >
            {SERVICES.map((service) => {
              const item = summary[service.key] || {};
              return (
                <Tab
                  key={service.key}
                  value={service.key}
                  label={
                    <Stack alignItems="flex-start" spacing={0.5}>
                      <Typography sx={{ fontWeight: 900 }}>
                        {serviceLabel(t, service.key)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: COLORS.muted }}>
                        {t('click_to_view') || 'انقر لعرض الخدمة'}
                      </Typography>
                      <Stack direction="row" spacing={0.75}>
                        <Chip
                          size="small"
                          label={`${item.pending || 0} ${
                            t('pending') || 'معلق'
                          }`}
                          color={(item.pending || 0) > 0 ? 'warning' : 'default'}
                        />
                        <Chip
                          size="small"
                          label={`${item.entered || 0} ${
                            t('entered') || 'مدخل'
                          }`}
                        />
                      </Stack>
                    </Stack>
                  }
                />
              );
            })}
          </Tabs>

          <Box sx={{ p: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.25}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <TextField
                  select
                  size="small"
                  label={t('filter_status') || 'الحالة'}
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  sx={{ minWidth: 190 }}
                >
                  <MenuItem value="all">{t('show_all') || 'عرض الكل'}</MenuItem>
                  <MenuItem value="pending">
                    {t('pending_accounting_entry') || 'بانتظار الإدخال'}
                  </MenuItem>
                  <MenuItem value="entered">
                    {t('entered_accounting_system') || 'مدخل في المحاسبة'}
                  </MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label={t('sort_by_date') || 'ترتيب حسب التاريخ'}
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  sx={{ minWidth: 170 }}
                >
                  <MenuItem value="desc">
                    {t('newest_first') || 'الأحدث أولًا'}
                  </MenuItem>
                  <MenuItem value="asc">
                    {t('oldest_first') || 'الأقدم أولًا'}
                  </MenuItem>
                </TextField>
              </Stack>
              <TextField
                size="small"
                label={t('search') || 'بحث'}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: { xs: '100%', md: 320 } }}
              />
            </Stack>

            {serviceErrors[activeService] ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {serviceErrors[activeService]}
              </Alert>
            ) : null}

            <TableContainer sx={{ ...smartTableContainerSx, width: '100%' }}>
              <Table size="small" sx={{ ...smartTableSx, minWidth: 1100 }}>
                <TableHead sx={smartTableHeadSx}>
                  <TableRow>
                    <TableCell width={90}>{t('record_id') || 'رقم السجل'}</TableCell>
                    <TableCell width={120}>{t('date') || 'التاريخ'}</TableCell>
                    <TableCell>{t('horse_name') || 'الخيل'}</TableCell>
                    <TableCell align="right" width={130}>
                      {t('amount') || 'المبلغ'}
                    </TableCell>
                    <TableCell width={190}>
                      {t('accounting_entry_status') || 'حالة الإدخال'}
                    </TableCell>
                    <TableCell width={190}>
                      {t('invoice_number') || 'رقم الفاتورة'}
                    </TableCell>
                    <TableCell>{t('notes') || 'ملاحظات'}</TableCell>
                    <TableCell align="center" width={150}>
                      {t('actions') || 'الإجراءات'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <CircularProgress size={26} />
                      </TableCell>
                    </TableRow>
                  ) : pagedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        {t('no_data') || 'لا توجد بيانات'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedRows.map((row, rowIndex) => (
                      <TableRow
                        key={getRowKey(
                          activeService,
                          row,
                          page * rowsPerPage + rowIndex
                        )}
                        hover
                      >
                        <TableCell>{row.record_id}</TableCell>
                        <TableCell>
                          <DateText value={row.record_date || row.start_date} />
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>
                            {row.horse_name || '-'}
                          </Typography>
                          {activeService === 'transportation' &&
                          (row.from_location || row.to_location) ? (
                            <Typography variant="caption" color="text.secondary">
                              {row.from_location || '-'} → {row.to_location || '-'}
                            </Typography>
                          ) : null}
                          {activeService === 'accommodation' ? (
                            <Stack spacing={0.25}>
                              <Typography variant="caption" color="text.secondary">
                                {isAccommodationBilling(row)
                                  ? <>{t('billing_period') || 'فترة الفوترة'}: <DateText value={row.period_start} /> - <DateText value={row.period_end} /></>
                                  : <><DateText value={row.start_date} /> - <DateText value={row.end_date} /></>}
                              </Typography>
                              {isAccommodationBilling(row) ? (
                                <Typography variant="caption" sx={{ color: COLORS.gold, fontWeight: 800 }}>
                                  {accommodationBillingLabel(t, row) + ` #${row.billing_cycle || 1}`}
                                  {row.type_name_ar || row.type_name_en
                                    ? ` · ${row.type_name_ar || row.type_name_en}`
                                    : ''}
                                  {isPartialAccommodation(row) && row.days_count
                                    ? ` · ${row.days_count} ${t('days') || 'days'}`
                                    : ''}
                                </Typography>
                              ) : null}
                            </Stack>
                          ) : null}
                          {activeService === 'reproduction' ? (
                            <Typography variant="caption" sx={{ color: COLORS.gold, fontWeight: 800, display: 'block' }}>
                              {row.procedure_name || row.event_type || '-'}
                              {row.result_status ? ` · ${row.result_status}` : ''}
                            </Typography>
                          ) : null}
                          {activeService === 'competitions' ? (
                            <Stack spacing={0.25}>
                              <Typography variant="caption" sx={{ color: COLORS.gold, fontWeight: 800 }}>
                                {row.competition_name || '-'}
                                {row.class_name ? ` · ${row.class_name}` : ''}
                                {row.championship_name ? ` · ${row.championship_name}` : ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t(String(row.transaction_type || '').toLowerCase()) || row.transaction_type}
                                {row.direction ? ` · ${t(String(row.direction).toLowerCase()) || row.direction}` : ''}
                                {row.owner_name ? ` · ${row.owner_name}` : ''}
                              </Typography>
                            </Stack>
                          ) : null}
                        </TableCell>
                        <TableCell align="right">
                          {formatMoney(getAmount(row))} SAR
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Checkbox
                              checked={isEntered(row)}
                              onChange={(e) =>
                                updateRow(row.record_id, {
                                  reviewed_by_accountant: e.target.checked,
                                })
                              }
                            />
                            <Chip
                              size="small"
                              color={isEntered(row) ? 'success' : 'warning'}
                              variant={isEntered(row) ? 'filled' : 'outlined'}
                              label={statusLabel(t, row)}
                            />
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={row.invoice_number || ''}
                            onChange={(e) =>
                              updateRow(row.record_id, {
                                invoice_number: e.target.value,
                              })
                            }
                            placeholder={t('invoice_number') || 'رقم الفاتورة'}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={row.notes || ''}
                            onChange={(e) =>
                              updateRow(row.record_id, {
                                notes: e.target.value,
                              })
                            }
                            placeholder={t('notes') || 'ملاحظات'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" justifyContent="center" spacing={0.5}>
                            <Tooltip title={t('details') || 'تفاصيل'}>
                              <IconButton onClick={() => setDialogRecord(row)}>
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            {!isEntered(row) ? (
                              <Tooltip
                                title={
                                  t('mark_entered_and_save') ||
                                  'تعليم كمدخل وحفظ'
                                }
                              >
                                <IconButton
                                  color="success"
                                  onClick={() => markEnteredAndSave(row)}
                                >
                                  <AssignmentTurnedInIcon />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                            <Tooltip title={t('save') || 'حفظ'}>
                              <IconButton color="primary" onClick={() => saveRow(row)}>
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 20, 50]}
              count={filteredRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
            />
          </Box>
        </Paper>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      <Dialog
        open={Boolean(dialogRecord)}
        onClose={() => setDialogRecord(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('operation_details') || 'تفاصيل العملية'}</DialogTitle>
        <DialogContent dividers>
          {dialogRecord ? (
            <Stack spacing={1.25}>
              <DetailRow label={t('service_type') || 'نوع الخدمة'}>
                {serviceLabel(t, activeService)}
              </DetailRow>
              <DetailRow label={t('record_id') || 'رقم السجل'}>
                {dialogRecord.record_id}
              </DetailRow>
              <DetailRow label={t('horse_name') || 'الخيل'}>
                {dialogRecord.horse_name || '-'}
              </DetailRow>
              <DetailRow label={t('date') || 'التاريخ'}>
                <DateText value={dialogRecord.record_date || dialogRecord.start_date} />
              </DetailRow>
              {activeService === 'accommodation' && isAccommodationBilling(dialogRecord) ? (
                <>
                  <DetailRow label={t('billing_period') || 'فترة الفوترة'}>
                    <DateText value={dialogRecord.period_start} /> - <DateText value={dialogRecord.period_end} />
                  </DetailRow>
                  <DetailRow label={t('billing_type') || 'نوع الفوترة'}>
                    {accommodationBillingLabel(t, dialogRecord)}
                  </DetailRow>
                  <DetailRow label={t('days_count') || 'عدد الأيام'}>
                    {dialogRecord.days_count || '-'}
                  </DetailRow>
                  <DetailRow label={t('accommodation_type') || 'نوع السكن'}>
                    {dialogRecord.type_name_ar || dialogRecord.type_name_en || '-'}
                  </DetailRow>
                  <DetailRow label={t('monthly_price') || 'السعر الشهري'}>
                    {formatMoney(dialogRecord.monthly_price)} SAR
                  </DetailRow>
                  <DetailRow label={t('vat') || 'VAT'}>
                    {formatMoney(dialogRecord.vat_amount)} SAR ({dialogRecord.vat_percentage || 0}%)
                  </DetailRow>
                </>
              ) : null}
              <DetailRow label={t('amount') || 'المبلغ'}>
                {formatMoney(getAmount(dialogRecord))} SAR
              </DetailRow>
              <DetailRow label={t('accounting_entry_status') || 'حالة الإدخال'}>
                {statusLabel(t, dialogRecord)}
              </DetailRow>
              <DetailRow label={t('invoice_number') || 'رقم الفاتورة'}>
                {dialogRecord.invoice_number || '-'}
              </DetailRow>
              <DetailRow label={t('notes') || 'ملاحظات'}>
                {dialogRecord.notes || '-'}
              </DetailRow>
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function SummaryCard({ title, value, helper, warning = false }) {
  return (
    <Paper
      elevation={0}
      sx={{
        ...cardSx,
        p: 2,
        backgroundColor: warning ? COLORS.roseSoft : COLORS.card,
        minHeight: 104,
      }}
    >
      <Stack spacing={0.75}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ color: warning ? COLORS.rose : COLORS.gold, fontWeight: 800 }}>
            {title}
          </Typography>
          {warning ? <WarningAmberIcon color="warning" fontSize="small" /> : null}
        </Stack>
        <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: COLORS.muted }}>
          {helper}
        </Typography>
      </Stack>
    </Paper>
  );
}

function DetailRow({ label, children }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="space-between"
      sx={{ borderBottom: `1px solid ${COLORS.line}`, pb: 1 }}
    >
      <Typography sx={{ color: COLORS.muted }}>{label}</Typography>
      <Typography sx={{ color: COLORS.text, fontWeight: 800 }}>{children}</Typography>
    </Stack>
  );
}
